import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

/* ─────────────────────────── Breakpoints ──────────────────────────────── */

const BREAKPOINTS = {
  sm: '576px',
  md: '768px',
  lg: '1024px',
  xl: '1200px',
} as const;

type BreakpointKey = keyof typeof BREAKPOINTS;
const BP_ORDER: BreakpointKey[] = ['sm', 'md', 'lg', 'xl'];

export type FormGridCollapse = BreakpointKey;

/** Responsive column spec — matches the engine's ColumnsSpec object form. */
export type ResponsiveColumns = {
  base?: number | string;
  sm?: number | string;
  md?: number | string;
  lg?: number | string;
  xl?: number | string;
};

/** Responsive colSpan per breakpoint. */
export type ResponsiveColSpan = { sm?: number; md?: number; lg?: number; xl?: number };

/* ─────────────────────────── FormGrid (root) ──────────────────────────── */

export interface FormGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Column configuration. Accepts:
   * - A number (1–12): creates that many equal `1fr` columns.
   * - A CSS string: used directly as `grid-template-columns` (e.g. `'2fr 1fr'`).
   * - A `ResponsiveColumns` object for multi-breakpoint grids.
   * Defaults to `2`.
   */
  columns?: number | string | ResponsiveColumns;
  /** Uniform gap. Defaults to `rem(20)`. */
  gap?: string;
  /** Override column gap independently. */
  columnGap?: string;
  /** Override row gap independently. */
  rowGap?: string;
  /**
   * Breakpoint below which the grid collapses to 1 column.
   * Ignored when `columns` is a `ResponsiveColumns` object (breakpoints are explicit).
   * Defaults to `'md'`.
   */
  collapse?: FormGridCollapse;
  semanticId?: string;
}

function resolveTemplate(columns: number | string | undefined): string {
  if (typeof columns === 'number' && Number.isFinite(columns) && columns > 0) {
    return `repeat(${columns}, 1fr)`;
  }
  if (typeof columns === 'string' && columns !== '') {
    return columns;
  }
  return 'repeat(2, 1fr)';
}

function isResponsiveColumns(
  columns: FormGridProps['columns'],
): columns is ResponsiveColumns {
  return typeof columns === 'object' && columns !== null;
}

function buildResponsiveCSS(spec: ResponsiveColumns): string {
  const base = spec.base != null ? resolveTemplate(spec.base) : 'repeat(1, 1fr)';
  let result = `grid-template-columns: ${base};`;

  for (const bp of BP_ORDER) {
    if (spec[bp] != null) {
      result += `\n@media (min-width: ${BREAKPOINTS[bp]}) { grid-template-columns: ${resolveTemplate(spec[bp])}; }`;
    }
  }
  return result;
}

const TRANSIENT_GRID = ['$template', '$gap', '$columnGap', '$rowGap', '$collapse', '$responsiveCSS'] as const;
const shouldForwardGrid = (prop: string | number): boolean =>
  !TRANSIENT_GRID.includes(prop as (typeof TRANSIENT_GRID)[number]);

const StyledFormGrid = styled.div.withConfig({ shouldForwardProp: shouldForwardGrid })<{
  $template?: string;
  $gap: string;
  $columnGap?: string;
  $rowGap?: string;
  $collapse?: string;
  $responsiveCSS?: string;
}>`
  box-sizing: border-box;
  display: grid;
  gap: ${({ $gap }) => $gap};
  ${({ $columnGap }) => $columnGap && css`column-gap: ${$columnGap};`}
  ${({ $rowGap }) => $rowGap && css`row-gap: ${$rowGap};`}
  width: 100%;

  ${({ $responsiveCSS, $template, $collapse }) =>
    $responsiveCSS
      ? css`${$responsiveCSS}`
      : css`
          grid-template-columns: 1fr;
          @media (min-width: ${$collapse}) {
            grid-template-columns: ${$template};
          }
        `}
`;

const FormGridRoot = React.forwardRef<HTMLDivElement, FormGridProps>(function FormGrid(
  {
    columns = 2,
    gap = rem(20),
    columnGap,
    rowGap,
    collapse = 'md',
    semanticId,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  const isResponsive = isResponsiveColumns(columns);
  const responsiveCSS = isResponsive ? buildResponsiveCSS(columns) : undefined;
  const template = !isResponsive ? resolveTemplate(columns) : undefined;
  const bp = !isResponsive ? BREAKPOINTS[collapse] : undefined;

  return (
    <StyledFormGrid
      ref={ref}
      $template={template}
      $gap={gap}
      $columnGap={columnGap}
      $rowGap={rowGap}
      $collapse={bp}
      $responsiveCSS={responsiveCSS}
      {...semanticProps}
      {...rest}
    />
  );
});

FormGridRoot.displayName = 'FormGrid';

/* ─────────────────────────── FormGrid.Item ─────────────────────────────── */

export interface FormGridItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * How many columns this item spans. Accepts:
   * - A number: applies at all breakpoints.
   * - A `ResponsiveColSpan` object: per-breakpoint control.
   */
  colSpan?: number | ResponsiveColSpan;
  /** How many rows this item spans. */
  rowSpan?: number;
  /** Explicit column start position (1-based). */
  colStart?: number;
  /** CSS `order` value for explicit rendering order within the grid. */
  order?: number;
  semanticId?: string;
}

function buildItemResponsiveCSS(
  colSpan?: number | ResponsiveColSpan,
  colStart?: number,
  order?: number,
): string | undefined {
  const parts: string[] = [];

  if (order != null) parts.push(`order: ${order};`);
  if (colStart != null) parts.push(`grid-column-start: ${colStart};`);

  if (typeof colSpan === 'number') {
    parts.push(`grid-column: span ${colSpan};`);
  } else if (colSpan != null) {
    for (const bp of BP_ORDER) {
      if (colSpan[bp] != null) {
        parts.push(`@media (min-width: ${BREAKPOINTS[bp]}) { grid-column: span ${colSpan[bp]}; }`);
      }
    }
  }

  return parts.length > 0 ? parts.join('\n') : undefined;
}

const TRANSIENT_ITEM = ['$itemCSS', '$rowSpan'] as const;
const shouldForwardItem = (prop: string | number): boolean =>
  !TRANSIENT_ITEM.includes(prop as (typeof TRANSIENT_ITEM)[number]);

const StyledFormGridItem = styled.div.withConfig({ shouldForwardProp: shouldForwardItem })<{
  $itemCSS?: string;
  $rowSpan?: number;
}>`
  min-width: 0;

  ${({ $itemCSS }) => $itemCSS && css`${$itemCSS}`}

  ${({ $rowSpan }) =>
    $rowSpan != null &&
    css`
      grid-row: span ${$rowSpan};
    `}
`;

const FormGridItem = React.forwardRef<HTMLDivElement, FormGridItemProps>(
  function FormGridItem({ colSpan, rowSpan, colStart, order, semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    const itemCSS = buildItemResponsiveCSS(colSpan, colStart, order);

    return (
      <StyledFormGridItem
        ref={ref}
        $itemCSS={itemCSS}
        $rowSpan={rowSpan}
        {...semanticProps}
        {...rest}
      />
    );
  },
);

FormGridItem.displayName = 'FormGrid.Item';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const FormGrid = Object.assign(FormGridRoot, {
  Item: FormGridItem,
});
